import { useContext, useReducer, createContext, useCallback } from "react";
import { useRequest } from "../../services/request";
import { CalUtcWithFormat } from "../../utilities/functions";

const Context = createContext();

export function useOdooInvoices() {
  return useContext(Context);
}

const initState = {
  list: [],
  journalEntriesList: [],
  odooAccountsList: [],
  listOdooNames: [],
  listOdooInvoicesReport: [],
  selected: null,
  selectedAccount: null,
  count: 1,
  page: 1,
  perPage: 10,
  length: 0,
  countEntries: 1,
  pageEntries: 1,
  perPageEntries: 10,
  lengthEntries: 0,
  status: "idle",
  error: null,
};
const reducer = (state, action) => {
  switch (action.type) {
    case "set_list":
      return { ...state, list: [...action.payload] };
    case "set_listJournalEntries":
      return { ...state, journalEntriesList: [...action.payload] };
    case "set_listOdooAccounts":
      return { ...state, odooAccountsList: [...action.payload] };
    case "set_listOdooNames":
      return { ...state, listOdooNames: [...action.payload] };
    case "set_listOdooInvoicesReport":
      return { ...state, listOdooInvoicesReport: [...action.payload] };
    case "set_selected":
      return { ...state, selected: { ...action.payload } };
    case "set_selectedAccount":
      return { ...state, selectedAccount: { ...action.payload } };
    case "set_count":
      return { ...state, count: action.payload };
    case "set_countEntries":
      return { ...state, countEntries: action.payload };
    case "add":
      const newList = [{ ...action.payload }, ...state.list];
      return { ...state, list: newList };
    case "addAccount":
      const newOdooAccountList = [
        { ...action.payload },
        ...state.odooAccountsList,
      ];
      return { ...state, odooAccountsList: newOdooAccountList };
    case "edit": {
      const modified = state.list.map((p) =>
        p.siteTransactionId === action.payload.id ? { ...p, statusId: 3 } : p,
      );
      return { ...state, list: modified };
    }

    case "editAccount":
      const modifiedAccount = state.odooAccountsList.map((p) =>
        p.id === action.payload.id ? { ...p, ...action.payload } : p,
      );
      return { ...state, odooAccountsList: modifiedAccount };
    case "delete":
      const filtered = state.odooAccountsList.filter(
        (p) => p.id !== action.payload,
      );
      return { ...state, odooAccountsList: filtered };
    case "status":
      return { ...state, status: action.payload };
    case "set_page":
      return { ...state, page: action.payload };
    case "set_pageEntries":
      return { ...state, pageEntries: action.payload };
    case "set_per_page":
      return { ...state, perPage: action.payload };
    case "set_per_page_entries":
      return { ...state, perPageEntries: action.payload };
    case "set_length":
      return { ...state, length: action.payload };
    case "set_length_entries":
      return { ...state, lengthEntries: action.payload };
    default:
      throw new Error(`Invalid dispatch type: ${action.type}`);
  }
};

export default function OdooInvoicesProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initState);
  const req = useRequest();
  const fetchList = useCallback(
    async (data, page, perPage) => {
      dispatch({ type: "status", payload: "fetching" });
      let headersData = {
        pageNumber: page,
      };
      if (perPage) headersData.pageSize = perPage;
      if (data?.fromDate) headersData.fromDate = CalUtcWithFormat(data?.fromDate);
      if (data?.toDate) headersData.toDate = CalUtcWithFormat(data?.toDate);
      if (data.isFailure) headersData.isFailure = data.isFailure;
      const resData = await req(
        `SiteOdooInvoice/List/${data?.siteId}`,
        null,
        {},
        true,
        headersData,
      );
      dispatch({ type: "status", payload: `idle` });
      dispatch({ type: "set_list", payload: resData.data.invoices || [] });
      dispatch({ type: "set_per_page", payload: resData.data.pageSize });
      dispatch({ type: "set_count", payload: resData.data.totalPages });
      dispatch({ type: "set_length", payload: resData.data.totalSize });
    },
    [req],
  );
  const fetchOdooAccountsList = useCallback(
    async (id) => {
      dispatch({ type: "status", payload: "fetching" });

      const resData = await req(`OdooAcount/List/${id}`, null, {}, true);
      dispatch({ type: "status", payload: `idle` });
      dispatch({ type: "set_listOdooAccounts", payload: resData.data || [] });
    },
    [req],
  );
  const send = useCallback(
    async (id) => {
      return new Promise(async (resolve, reject) => {
        dispatch({ type: "status", payload: `creating` });
        try {
          const resData = await req(
            `SiteOdooInvoice/Send/${id}`,
            null,
            { method: "PUT" },
            true,
          );
          dispatch({ type: "edit", payload: { id: id } });
          resolve(resData);
        } catch (e) {
          reject(e);
        } finally {
          dispatch({ type: "status", payload: `idle` });
        }
      });
    },
    [req],
  );

  const fetchJournalList = useCallback(
    async (data, page, perPage) => {
      dispatch({ type: "status", payload: "fetching" });
      let headersData = {
        pageNumber: page,
      };
      if (perPage) headersData.pageSize = perPage;
      if (data.fromDate) headersData.fromDate = CalUtcWithFormat(data.fromDate);
      if (data.toDate) headersData.toDate = CalUtcWithFormat(data.toDate);
      if (data.isFailure) headersData.isFailure = data.isFailure;
      const resData = await req(
        `ShiftOdooJournalEntry/List/${data.companyId}, ${data?.siteId}`,
        null,
        {},
        true,
        headersData,
      );
      dispatch({ type: "status", payload: `idle` });
      dispatch({
        type: "set_listJournalEntries",
        payload: resData.data.journalEntrys || [],
      });
      dispatch({
        type: "set_per_page_entries",
        payload: resData.data.pageSize,
      });
      dispatch({ type: "set_countEntries", payload: resData.data.totalPages });
      dispatch({ type: "set_length_entries", payload: resData.data.totalSize });
    },
    [req],
  );

  const sendEntry = useCallback(
    async (id) => {
      return new Promise(async (resolve, reject) => {
        dispatch({ type: "status", payload: `creating` });
        try {
          const resData = await req(
            `ShiftOdooJournalEntry/Send/${id}`,
            null,
            { method: "PUT" },
            true,
          );
          dispatch({ type: "edit", payload: { id: id } });
          resolve(resData);
        } catch (e) {
          reject(e);
        } finally {
          dispatch({ type: "status", payload: `idle` });
        }
      });
    },
    [req],
  );

  const create = useCallback(
    async (data) => {
      return new Promise(async (resolve, reject) => {
        const newData = { ...data };
        try {
          const resData = await req(
            "OdooAcount/Add",
            newData,
            { method: "POST" },
            true,
          );
          dispatch({ type: "addAccount", payload: resData.data });
          dispatch({ type: "status", payload: `idle` });
          resolve(resData);
        } catch (e) {
          reject(e);
        } finally {
          dispatch({ type: "status", payload: `idle` });
        }
      });
    },
    [req],
  );

  const edit = useCallback(
    async (data, id) => {
      return new Promise(async (resolve, reject) => {
        dispatch({ type: "status", payload: `creating` });
        try {
          const resData = await req(
            `OdooAcount/Update/${id}`,
            data,
            { method: "PUT" },
            true,
          );
          dispatch({ type: "editAccount", payload: resData.data });
          resolve(resData);
        } catch (e) {
          reject(e);
        } finally {
          dispatch({ type: "status", payload: `idle` });
        }
      });
    },
    [req],
  );

  const remove = useCallback(
    async (id) => {
      return new Promise(async (resolve, reject) => {
        try {
          dispatch({ type: "status", payload: `deleting ${id}` });
          const resData = await req(
            `OdooAcount/Delete/${id}/`,
            {},
            { method: "DELETE" },
            true,
          );
          dispatch({ type: "status", payload: "idle" });
          dispatch({ type: "delete", payload: id });
          resolve(resData);
        } catch (error) {
          reject(error);
        }
      });
    },
    [req],
  );

  const fetchOdooAccountsNamesList = useCallback(async () => {
    dispatch({ type: "status", payload: "fetching" });

    const resData = await req(`OdooAcount/Names`, null, {}, true);
    dispatch({ type: "status", payload: `idle` });
    dispatch({ type: "set_listOdooNames", payload: resData.data || [] });
  }, [req]);

  const SendAllInvoices = useCallback(
    async (id, data) => {
      return new Promise(async (resolve, reject) => {
        dispatch({ type: "status", payload: `creating` });
        let sendAllHeaders={};
        if (data?.fromDate) sendAllHeaders.fromDate = CalUtcWithFormat(data?.fromDate);
        if (data?.toDate) sendAllHeaders.toDate = CalUtcWithFormat(data?.toDate);
        try {
          const resData = await req(
            `SiteOdooInvoice/SendAll/${id}`,
            {},
            { method: "PUT" },
            true,
            sendAllHeaders,
          );
          dispatch({ type: "edit", payload: { id: id } });
          resolve(resData);
        } catch (e) {
          reject(e);
        } finally {
          dispatch({ type: "status", payload: `idle` });
        }
      });
    },
    [req],
  );

  const fetchListOdooInvoicesReport = useCallback(
    async (data, page, perPage) => {
      dispatch({ type: "status", payload: "fetching" });
      let headersData = {
        pageNumber: page,
      };
      if (perPage) headersData.pageSize = perPage;
      if (data.fromDate) headersData.fromDate = CalUtcWithFormat(data.fromDate);
      if (data.toDate) headersData.toDate = CalUtcWithFormat(data.toDate);
      if (data.isFailure) headersData.isFailure = data.isFailure;
      const resData = await req(
        `SiteOdooInvoice/Report/${data?.companyId}`,
        null,
        {},
        true,
        headersData,
      );
      dispatch({ type: "status", payload: `idle` });
      dispatch({
        type: "set_listOdooInvoicesReport",
        payload: resData.data.invoices || [],
      });
      dispatch({ type: "set_per_page", payload: resData.data.pageSize });
      dispatch({ type: "set_count", payload: resData.data.totalPages });
      dispatch({ type: "set_length", payload: resData.data.totalSize });
    },
    [req],
  );
  return (
    <Context.Provider
      value={{
        state,
        dispatch,
        fetchList,
        send,
        fetchJournalList,
        sendEntry,
        fetchOdooAccountsList,
        create,
        edit,
        remove,
        fetchOdooAccountsNamesList,
        SendAllInvoices,
        fetchListOdooInvoicesReport,
      }}
    >
      {children}
    </Context.Provider>
  );
}
